import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
    LoadingDiv,
    SweetAlertShow,
} from "../../../master/Master";

import Header from "../../../master/SideBar";
import Nav from "../../../master/NavBar";
import Master from "../../Master";

import {
    busList,
    busStopsList,
    busStudentsList,
} from "../../../../api/Transport";
import Select from "react-select";
import {
    busStudentTransfer,
    studentBusData,
} from "../../../../api/TransportService";

export default function StudentBusTransfer() {
    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);

    const urlStudentId = params.get("student_id");
    const urlTransportNFeeId = params.get("transport_n_fee_id");

    const today = new Date().toISOString().split("T")[0];

    const [loading, setLoading] = useState(false);

    const [students, setStudents] = useState([]);
    const [buses, setBuses] = useState([]);
    const [stops, setStops] = useState([]);
    const [newStops, setNewStops] = useState([]);
    const [transportReport, setTransportReport] = useState(null);
    const [busFeeDetails, setBusFeeDetails] = useState([]);

    const [selectedStudentId, setSelectedStudentId] = useState(
        urlStudentId ? Number(urlStudentId) : ""
    );

    const [currentEnrollment, setCurrentEnrollment] = useState(null);

    const [form, setForm] = useState({
        current_bus_id: "",
        current_stop_id: "",
        new_bus_id: "",
        new_stop_id: "",
        joining_date: today,
        payment_type: "",
        reason: "",
        discount_remark: "",
    });

    // =========================================================
    // HELPERS
    // =========================================================

    const money = (value) =>
        `₹${Number(value || 0).toLocaleString("en-IN")}`;

    const getBusId = (item) =>
        item?.bus_id ?? item?.bus?.id ?? "";

    const getStopId = (item) =>
        item?.bus_stop_id ?? item?.stop?.id ?? "";

    // =========================================================
    // UNIQUE STUDENTS
    // =========================================================

    const uniqueStudents = useMemo(() => {
        const map = new Map();

        students.forEach((item) => {
            const studentId = Number(item?.student_id);

            if (!studentId) return;

            if (!map.has(studentId)) {
                map.set(studentId, item);
            }
        });

        return Array.from(map.values());
    }, [students]);


    const currentBus = useMemo(() => {
        return buses.find(
            (bus) =>
                Number(bus?.id) ===
                Number(form?.current_bus_id)
        );
    }, [buses, form?.current_bus_id]);


    const newBus = useMemo(() => {
        return buses.find(
            (bus) =>
                Number(bus?.id) ===
                Number(form?.new_bus_id)
        );
    }, [buses, form?.new_bus_id]);


    const currentBusStops = useMemo(() => {
        if (!form?.current_bus_id) {
            return [];
        }

        return stops.filter(
            (stop) =>
                Number(stop?.bus_id) ===
                Number(form?.current_bus_id)
        );
    }, [stops, form?.current_bus_id]);

    const newBusStops = useMemo(() => {
        if (!form?.new_bus_id) {
            return [];
        }

        return newStops.filter(
            (stop) =>
                Number(stop?.bus_id) ===
                Number(form?.new_bus_id)
        );
    }, [newStops, form?.new_bus_id]);


    const currentStop = useMemo(() => {
        return stops.find(
            (stop) =>
                Number(stop?.id) ===
                Number(form?.current_stop_id)
        );
    }, [stops, form?.current_stop_id]);

    const newStop = useMemo(() => {
        return newStops.find(
            (stop) =>
                Number(stop?.id) ===
                Number(form?.new_stop_id)
        );
    }, [newStops, form?.new_stop_id]);

    const detailPaidFee = busFeeDetails.reduce(
        (sum, item) =>
            sum + Number(item?.paid_amount ?? 0),
        0
    );

    const currentPaidFee = Math.max(
        Number(currentEnrollment?.total_paid ?? 0),
        detailPaidFee
    );

    const transferBlocked = currentPaidFee > 0;

    // =========================================================
    // FEES
    // =========================================================

    const currentStopFee = Number(
        currentEnrollment?.bus_stop_fee ?? 0
    );

    const currentDiscount = Number(
        currentEnrollment?.total_discount ?? 0
    );

    const currentFinalFee = Number(
        currentEnrollment?.total_final_fee ??
        currentEnrollment?.final_fee ??
        Math.max(
            0,
            currentStopFee - currentDiscount
        )
    );

    const getNewStopFee = (stop) => {
        return Number(
            stop?.stop_fee ??
            stop?.fee ??
            stop?.amount ??
            stop?.bus_stop_fee ??
            stop?.final_fee ??
            0
        );
    };

    const newStopFee = getNewStopFee(newStop);

    const [newDiscount, setNewDiscount] = useState(0);

    const newFinalFee = Math.max(
        0,
        newStopFee - Number(newDiscount || 0)
    );

    const handleNewDiscountChange = (value) => {
        let discount = Number(value || 0);

        if (discount < 0) discount = 0;
        if (discount > newStopFee) discount = newStopFee;

        setNewDiscount(discount);
    };

    // =========================================================
    // NEW FEE CREATION PREVIEW
    // =========================================================

    const getPeriodMonths = (type) => {
        switch (type) {
            case "Quarterly":
                return 3;
            case "Half Year":
                return 6;
            case "Full Year":
                return 12;
            case "Monthly":
            default:
                return 1;
        }
    };

    const feePreview = useMemo(() => {
        if (
            !form?.joining_date ||
            !form?.payment_type ||
            !form?.new_stop_id
        ) {
            return [];
        }

        const start = new Date(`${form.joining_date}T00:00:00`);

        if (Number.isNaN(start.getTime())) {
            return [];
        }

        // Preview joining month se current month tak hi banega.
        // Monthly   = har month 1 record
        // Quarterly = 3 months ka 1 record
        // Half Year = 6 months ka 1 record
        // Full Year = pure period ka 1 record
        //
        // IMPORTANT: Har record me ek hi stop fee lagegi.
        // Quarterly/Half Year/Full Year me stop fee ko 3/6/12 se multiply nahi karna.
        const periodMonths = getPeriodMonths(form.payment_type);

        const now = new Date();
        const currentMonthStart = new Date(
            now.getFullYear(),
            now.getMonth(),
            1
        );

        const joiningMonthStart = new Date(
            start.getFullYear(),
            start.getMonth(),
            1
        );

        // Joining date future me hai to preview nahi.
        if (joiningMonthStart > currentMonthStart) {
            return [];
        }

        const totalMonths =
            (currentMonthStart.getFullYear() -
                joiningMonthStart.getFullYear()) *
            12 +
            (currentMonthStart.getMonth() -
                joiningMonthStart.getMonth()) +
            1;

        const recordCount = Math.ceil(
            totalMonths / periodMonths
        );

        const stopFee = Number(newStopFee || 0);
        const discount = Math.min(
            Math.max(Number(newDiscount || 0), 0),
            stopFee
        );

        return Array.from({ length: recordCount }, (_, index) => {
            const startMonthIndex = index * periodMonths;

            const monthsInRecord = Math.min(
                periodMonths,
                totalMonths - startMonthIndex
            );

            const periodStart = new Date(
                joiningMonthStart.getFullYear(),
                joiningMonthStart.getMonth() + startMonthIndex,
                1
            );

            const periodEnd = new Date(
                joiningMonthStart.getFullYear(),
                joiningMonthStart.getMonth() +
                startMonthIndex +
                monthsInRecord - 1,
                1
            );

            // Har bus_fee_details record me stop ki ek hi fee rahegi.
            const actual = stopFee;
            const final = Math.max(
                0,
                actual - discount
            );

            return {
                month: periodStart.getMonth() + 1,
                year: periodStart.getFullYear(),
                monthName: periodStart.toLocaleString(
                    "en-IN",
                    { month: "long" }
                ),
                startMonthName: periodStart.toLocaleString(
                    "en-IN",
                    { month: "long" }
                ),
                endMonthName: periodEnd.toLocaleString(
                    "en-IN",
                    { month: "long" }
                ),
                months: monthsInRecord,
                type: form.payment_type,
                actual,
                discount,
                final,
            };
        });
    }, [
        form?.joining_date,
        form?.payment_type,
        form?.new_stop_id,
        newStopFee,
        newDiscount,
    ]);

    // =========================================================
    // STUDENT TRANSPORT REPORT
    // =========================================================

    const getStudentTransportReport = async (studentId, transportNFeeId) => {
        if (!studentId || !transportNFeeId) return null;

        try {
            setLoading(true);

            const response = await studentBusData(
                `student_id=${studentId}&transport_n_fee_id=${transportNFeeId}`
            );

            if (!response?.data?.status) {
                setTransportReport(null);
                setBusFeeDetails([]);
                setCurrentEnrollment(null);

                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ??
                    "Student transport details not found."
                );

                return null;
            }

            // API ab sirf ek BusStudent/transport enrollment return karti hai.
            const transport = response?.data?.data ?? null;

            setTransportReport(transport);
            setCurrentEnrollment(transport);
            setBusFeeDetails(
                Array.isArray(transport?.bus_fee_details)
                    ? transport.bus_fee_details
                    : []
            );

            if (transport) {
                setForm((prev) => ({
                    ...prev,
                    current_bus_id: getBusId(transport),
                    current_stop_id: getStopId(transport),
                    new_bus_id: "",
                    new_stop_id: "",
                    // Database me saved joining date hi show hogi.
                    joining_date: transport?.joining_date || today,
                    payment_type: "",
                    reason: "",
                    discount_remark: "",
                }));

                setNewDiscount(0);
                setNewStops([]);
            } else {
                setForm((prev) => ({
                    ...prev,
                    current_bus_id: "",
                    current_stop_id: "",
                    new_bus_id: "",
                    new_stop_id: "",
                    joining_date: today,
                    payment_type: "",
                    reason: "",
                    discount_remark: "",
                }));

                setNewDiscount(0);
                setNewStops([]);
            }

            return transport;
        } catch (error) {
            setTransportReport(null);
            setBusFeeDetails([]);
            setCurrentEnrollment(null);

            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ??
                error?.message ??
                "Unable to load student transport details."
            );

            return null;
        } finally {
            setLoading(false);
        }
    };

    const getStudents = async () => {
        try {
            setLoading(true);

            const response =
                await busStudentsList("limit=all");

            if (response?.data?.status) {
                const list =
                    response?.data?.data ?? [];

                setStudents(list);

                // URL student/enrollment auto select
                if (
                    urlStudentId ||
                    urlTransportNFeeId
                ) {
                    const found = list.find((item) => {
                        const studentMatch =
                            urlStudentId &&
                            Number(item?.student_id) ===
                            Number(urlStudentId);

                        const enrollmentMatch =
                            urlTransportNFeeId &&
                            Number(item?.id) ===
                            Number(urlTransportNFeeId);

                        return (
                            studentMatch ||
                            enrollmentMatch
                        );
                    });

                    if (found) {
                        selectStudentEnrollment(found);
                    }
                }
            } else {
                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ??
                    "Students not found."
                );
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ??
                error?.message ??
                "Unable to load students."
            );
        } finally {
            setLoading(false);
        }
    };

    const getBuses = async () => {
        try {
            const response =
                await busList("limit=all");

            if (response?.data?.status) {
                setBuses(
                    response?.data?.data ?? []
                );
            } else {
                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ??
                    "Buses not found."
                );
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ??
                error?.message ??
                "Unable to load buses."
            );
        }
    };

    const getStops = async () => {
        try {
            const response =
                await busStopsList("limit=all");

            if (response?.data?.status) {
                setStops(
                    response?.data?.data ?? []
                );
            } else {
                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ??
                    "Stops not found."
                );
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ??
                error?.message ??
                "Unable to load stops."
            );
        }
    };

    useEffect(() => {
        getStudents();
        getBuses();
        getStops();
    }, []);

    const selectStudentEnrollment = async (entry) => {
        if (!entry) return;

        const studentId = Number(entry?.student_id);

        setSelectedStudentId(studentId);

        await getStudentTransportReport(
            studentId,
            Number(entry?.id)
        );
    };

    const handleStudentChange = async (value) => {
        const studentId = Number(value);

        setSelectedStudentId(
            studentId || ""
        );

        if (!studentId) {
            setCurrentEnrollment(null);
            setTransportReport(null);
            setBusFeeDetails([]);
            setNewStops([]);

            setForm({
                current_bus_id: "",
                current_stop_id: "",
                new_bus_id: "",
                new_stop_id: "",
                joining_date: today,
                payment_type: "",
                reason: "",
                discount_remark: "",
            });

            setNewDiscount(0);
            return;
        }

        const entry = students.find(
            (item) => Number(item?.student_id) === studentId
        );

        if (!entry?.id) {
            SweetAlertShow(
                "error",
                "Error",
                "Transport enrollment not found."
            );
            return;
        }

        await getStudentTransportReport(
            studentId,
            Number(entry.id)
        );
    };

    const handleChange = (
        field,
        value
    ) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };


    const handleCurrentBusChange = (
        value
    ) => {
        setForm((prev) => ({
            ...prev,
            current_bus_id: value,
            current_stop_id: "",
        }));
    };

    const handleNewBusChange = async (
        value
    ) => {
        setForm((prev) => ({
            ...prev,
            new_bus_id: value,
            new_stop_id: "",
        }));

        setNewDiscount(0);
        handleChange("discount_remark", "");

        if (!value) {
            setNewStops([]);
            return;
        }

        try {
            setLoading(true);

            // New bus select hote hi us bus ke stops
            // fresh API se load honge, fee ke saath.
            const response = await busStopsList(
                `bus_id=${value}&limit=all`
            );

            if (response?.data?.status) {
                setNewStops(response?.data?.data ?? []);
            } else {
                setNewStops([]);
                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ??
                    "Stops not found for selected bus."
                );
            }
        } catch (error) {
            setNewStops([]);
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ??
                error?.message ??
                "Unable to load stops for selected bus."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleNewStopChange = async (value) => {
        handleChange(
            "new_stop_id",
            value
        );

        setNewDiscount(0);
        handleChange("discount_remark", "");

        if (!value || !form?.new_bus_id) {
            return;
        }

        try {
            setLoading(true);

            // Selected stop ki fresh fee API se verify/load karo.
            const response = await busStopsList(
                `bus_id=${form.new_bus_id}&limit=all`
            );

            if (response?.data?.status) {
                setNewStops(response?.data?.data ?? []);
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ??
                error?.message ??
                "Unable to load selected stop fee."
            );
        } finally {
            setLoading(false);
        }
    };


    const handleReset = () => {
        if (
            urlStudentId ||
            urlTransportNFeeId
        ) {
            const found = students.find(
                (item) => {
                    const studentMatch =
                        urlStudentId &&
                        Number(item?.student_id) ===
                        Number(urlStudentId);

                    const enrollmentMatch =
                        urlTransportNFeeId &&
                        Number(item?.id) ===
                        Number(urlTransportNFeeId);

                    return (
                        studentMatch ||
                        enrollmentMatch
                    );
                }
            );

            if (found) {
                selectStudentEnrollment(found);
                return;
            }
        }

        setSelectedStudentId("");
        setCurrentEnrollment(null);
        setTransportReport(null);
        setBusFeeDetails([]);
        setNewStops([]);

        setForm({
            current_bus_id: "",
            current_stop_id: "",
            new_bus_id: "",
            new_stop_id: "",
            joining_date: today,
            payment_type: "",
            reason: "",
            discount_remark: "",
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedStudentId) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please select student."
            );
            return;
        }

        if (!currentEnrollment?.id) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Current bus enrollment not found."
            );
            return;
        }

        if (transferBlocked) {
            SweetAlertShow(
                "error",
                "Transfer Not Allowed",
                `Student has already paid ${money(currentPaidFee)} in transport fee. This student cannot be transferred.`
            );
            return;
        }

        if (!form?.current_bus_id) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please select current bus."
            );
            return;
        }

        if (!form?.current_stop_id) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please select current stop."
            );
            return;
        }

        if (!form?.new_bus_id) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please select new bus."
            );
            return;
        }

        if (
            Number(form?.current_bus_id) ===
            Number(form?.new_bus_id)
        ) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "New bus must be different from current bus."
            );
            return;
        }


        if (!form?.new_stop_id) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please select new stop."
            );
            return;
        }

        if (!form?.joining_date) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please select joining date."
            );
            return;
        }

        if (!form?.payment_type) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please select payment type."
            );
            return;
        }

        if (!form?.reason?.trim()) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Please enter transfer reason."
            );
            return;
        }

        // Discount diya hai to Discount Remark mandatory hai
        if (
            Number(newDiscount || 0) > 0 &&
            !form?.discount_remark?.trim()
        ) {
            SweetAlertShow(
                "error",
                "Validation Error",
                "Discount Remark is required when discount is given."
            );
            return;
        }

        setLoading(true);

        try {
            const formData =
                new FormData();

            formData.append(
                "student_id",
                selectedStudentId
            );

            formData.append(
                "enrollment_id",
                currentEnrollment.id
            );

            formData.append(
                "old_bus_id",
                form.current_bus_id
            );

            formData.append(
                "old_stop_id",
                form.current_stop_id
            );

            formData.append(
                "new_bus_id",
                form.new_bus_id
            );

            formData.append(
                "new_stop_id",
                form.new_stop_id
            );

            formData.append(
                "joining_date",
                form.joining_date
            );

            formData.append(
                "payment_type",
                form.payment_type
            );

            formData.append(
                "reason",
                form.reason.trim()
            );

            formData.append(
                "discount",
                Number(newDiscount || 0)
            );

            formData.append(
                "discount_remark",
                form.discount_remark?.trim() || ""
            );

            formData.append(
                "fees",
                JSON.stringify(
                    feePreview.map((item) => ({
                        month: Number(item?.month),
                        year: Number(item?.year),
                        type: item?.type,
                        actual_amount: Number(item?.actual || 0),
                        discount: Number(item?.discount || 0),
                        final_amount: Number(item?.final || 0),
                    }))
                )
            );

            const response =
                await busStudentTransfer(
                    formData
                );

            if (response?.data?.status) {
                SweetAlertShow(
                    "success",
                    "Transfer Successful",
                    response?.data?.message ??
                    "Student transferred successfully."
                );

                setTimeout(() => {
                    navigate(
                        `/student/details?id=${selectedStudentId}`
                    );
                }, 800);
            } else {
                SweetAlertShow(
                    "info",
                    "Warning",
                    response?.data?.message ??
                    "Transfer failed."
                );
            }
        } catch (error) {
            SweetAlertShow(
                "error",
                "Transfer Failed",
                error?.response?.data?.message ??
                error?.response?.message ??
                error?.message ??
                "Something went wrong."
            );
        } finally {
            setLoading(false);
        }
    };



    return (
        <>
            {loading && <LoadingDiv />}

            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">

                    <Header />

                    <div className="layout-page">

                        <Nav />

                        <div className="content-wrapper">

                            <div className="container-fluid flex-grow-1 container-p-y">

                                <div className="row">

                                    <Master />

                                    <div className="col-12">



                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">

                                            <div className="d-flex align-items-center gap-3">

                                                <div
                                                    className="d-flex align-items-center justify-content-center bg-primary text-white rounded-3 shadow-sm"
                                                    style={{
                                                        width: "50px",
                                                        height: "50px",
                                                    }}
                                                >
                                                    <i className="ri-bus-2-line fs-4"></i>
                                                </div>

                                                <div>
                                                    <h4 className="fw-bold mb-1">
                                                        Student Bus Transfer
                                                    </h4>

                                                    <p className="text-muted mb-0">
                                                        Change student's bus and stop
                                                        assignment
                                                    </p>
                                                </div>

                                            </div>

                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() =>
                                                    navigate(-1)
                                                }
                                            >
                                                <i className="ri-arrow-left-line me-1"></i>
                                                Back
                                            </button>

                                        </div>



                                        <div className="card border-0 shadow-sm mb-4">

                                            <div className="card-header py-3 border-bottom">

                                                <div className="d-flex align-items-center gap-2">

                                                    <div className="text-primary">
                                                        <i className="ri-user-search-line fs-5"></i>
                                                    </div>

                                                    <div>
                                                        <h6 className="mb-0 fw-semibold">
                                                            Select Student
                                                        </h6>

                                                        <small className="text-muted">
                                                            Search and select student for bus transfer
                                                        </small>
                                                    </div>

                                                </div>

                                            </div>

                                            <div className="card-body">

                                                <div className="col-12 col-lg-6">

                                                    <label className="form-label fw-semibold">
                                                        Student
                                                        <span className="text-danger ms-1">
                                                            *
                                                        </span>
                                                    </label>

                                                    <Select
                                                        className="student-select"
                                                        classNamePrefix="student"
                                                        placeholder="Search student by name or registration no..."
                                                        isSearchable={true}
                                                        isClearable={true}
                                                        options={uniqueStudents.map((item) => ({
                                                            value: Number(item?.student_id),
                                                            label: `${item?.student?.name ?? "-"
                                                                } — Reg. ${item?.student?.registration_no ?? "-"
                                                                }`,
                                                            student: item,
                                                        }))}
                                                        value={
                                                            uniqueStudents
                                                                .map((item) => ({
                                                                    value: Number(item?.student_id),
                                                                    label: `${item?.student?.name ?? "-"
                                                                        } — Reg. ${item?.student?.registration_no ?? "-"
                                                                        }`,
                                                                    student: item,
                                                                }))
                                                                .find(
                                                                    (option) =>
                                                                        Number(option.value) ===
                                                                        Number(selectedStudentId)
                                                                ) || null
                                                        }
                                                        onChange={(selected) => {
                                                            handleStudentChange(
                                                                selected?.value || ""
                                                            );
                                                        }}
                                                        noOptionsMessage={() =>
                                                            "Student not found"
                                                        }
                                                        loadingMessage={() =>
                                                            "Loading students..."
                                                        }
                                                    />

                                                    <small className="text-muted mt-2 d-block">
                                                        <i className="ri-search-line me-1"></i>
                                                        Type student name or registration number to search.
                                                    </small>

                                                </div>

                                            </div>

                                        </div>
                                        {currentEnrollment && (

                                            <form
                                                onSubmit={handleSubmit}
                                            >


                                                <div className="row g-4 mb-4">


                                                    <div className="col-12 col-xl-6">

                                                        <div className="card border-0 shadow-sm h-100">

                                                            <div className="card-header py-3 border-bottom bg-light">

                                                                <div className="d-flex align-items-center gap-2">

                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center rounded-3 bg-warning-subtle text-warning"
                                                                        style={{
                                                                            width: "40px",
                                                                            height: "40px",
                                                                        }}
                                                                    >
                                                                        <i className="ri-map-pin-user-line fs-5"></i>
                                                                    </div>

                                                                    <div>

                                                                        <h6 className="mb-0 fw-semibold">
                                                                            Current Assignment
                                                                        </h6>

                                                                        <small className="text-muted">
                                                                            Existing bus and stop
                                                                        </small>

                                                                    </div>

                                                                </div>

                                                            </div>

                                                            <div className="card-body">

                                                                {/* Current Bus */}


                                                                <div className="mb-3">
                                                                    <label className="form-label fw-semibold">
                                                                        Current Bus
                                                                    </label>

                                                                    <input
                                                                        type="text"
                                                                        className="form-control bg-light"
                                                                        value={
                                                                            currentBus?.bus_number
                                                                                ? `${currentBus.bus_number}${currentBus?.driver_name
                                                                                    ? ` — ${currentBus.driver_name}`
                                                                                    : ""
                                                                                }`
                                                                                : ""
                                                                        }
                                                                        readOnly
                                                                    />
                                                                </div>


                                                                {/* Current Stop */}

                                                                <div className="mb-3">
                                                                    <label className="form-label fw-semibold">
                                                                        Current Stop
                                                                    </label>

                                                                    <input
                                                                        type="text"
                                                                        className="form-control bg-light"
                                                                        value={
                                                                            currentStop?.stop_name || ""
                                                                        }
                                                                        readOnly
                                                                    />
                                                                </div>

                                                                {/* Current Info */}

                                                                {currentBus && (

                                                                    <div className="rounded-3 bg-light p-3">

                                                                        <div className="row g-3">

                                                                            <div className="col-6">

                                                                                <small className="text-muted d-block">
                                                                                    Driver
                                                                                </small>

                                                                                <strong>
                                                                                    {currentBus
                                                                                        ?.driver_name ??
                                                                                        "-"}
                                                                                </strong>

                                                                            </div>

                                                                            <div className="col-6">

                                                                                <small className="text-muted d-block">
                                                                                    Mobile
                                                                                </small>

                                                                                <strong>
                                                                                    {currentBus
                                                                                        ?.driver_number ??
                                                                                        "-"}
                                                                                </strong>

                                                                            </div>

                                                                            <div className="col-12">

                                                                                <small className="text-muted d-block">
                                                                                    Selected Stop
                                                                                </small>

                                                                                <strong>
                                                                                    {currentStop
                                                                                        ?.stop_name ??
                                                                                        "Not selected"}
                                                                                </strong>

                                                                            </div>

                                                                        </div>

                                                                    </div>

                                                                )}

                                                                {/* Current Fee */}

                                                                <div className="rounded-3 bg-light border p-3 mt-3">

                                                                    <div className="d-flex align-items-center gap-2 mb-3">
                                                                        <i className="ri-money-rupee-circle-line text-warning fs-5"></i>
                                                                        <strong>Current Fee</strong>
                                                                    </div>

                                                                    <div className="row g-3">
                                                                        <div className="col-4">
                                                                            <small className="text-muted d-block">Stop Fee</small>
                                                                            <strong>{money(currentStopFee)}</strong>
                                                                        </div>

                                                                        <div className="col-4">
                                                                            <small className="text-muted d-block">Discount</small>
                                                                            <strong className="text-warning">
                                                                                {money(currentDiscount)}
                                                                            </strong>
                                                                        </div>

                                                                        <div className="col-4">
                                                                            <small className="text-muted d-block">Final Fee</small>
                                                                            <strong className="text-success">
                                                                                {money(currentFinalFee)}
                                                                            </strong>
                                                                        </div>
                                                                    </div>

                                                                </div>

                                                                {/* Existing Bus Fee Details */}

                                                                <div className="mt-3">
                                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                                        <strong className="small">
                                                                            Bus Fee Details
                                                                        </strong>
                                                                        <span className="badge bg-light text-dark">
                                                                            {busFeeDetails.length} Record{busFeeDetails.length === 1 ? "" : "s"}
                                                                        </span>
                                                                    </div>

                                                                    {busFeeDetails.length > 0 ? (
                                                                        <div className="table-responsive border rounded-3">
                                                                            <table className="table table-sm align-middle mb-0">
                                                                                <thead className="table-light">
                                                                                    <tr>
                                                                                        <th>Month</th>
                                                                                        <th>Type</th>
                                                                                        <th>Actual</th>
                                                                                        <th>Discount</th>
                                                                                        <th>Final</th>
                                                                                        <th>Paid</th>
                                                                                        <th>Due</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {busFeeDetails.map((item) => (
                                                                                        <tr key={item?.id}>
                                                                                            <td>{item?.month}/{item?.year}</td>
                                                                                            <td>{item?.type ?? "-"}</td>
                                                                                            <td>{money(item?.actual_amount)}</td>
                                                                                            <td>{money(item?.discount)}</td>
                                                                                            <td>{money(item?.final_amount)}</td>
                                                                                            <td className={Number(item?.paid_amount || 0) > 0 ? "text-danger fw-semibold" : ""}>
                                                                                                {money(item?.paid_amount)}
                                                                                            </td>
                                                                                            <td>{money(item?.due_amount)}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-muted small border rounded-3 p-3">
                                                                            No bus fee details found.
                                                                        </div>
                                                                    )}
                                                                </div>

                                                            </div>

                                                        </div>

                                                    </div>

                                                    <div className="col-12 col-xl-6">

                                                        <div className="card border-primary shadow-sm h-100">

                                                            <div className="card-header py-3 border-bottom bg-primary-subtle">

                                                                <div className="d-flex align-items-center gap-2">

                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                                                                        style={{
                                                                            width: "40px",
                                                                            height: "40px",
                                                                        }}
                                                                    >
                                                                        <i className="ri-route-line fs-5"></i>
                                                                    </div>

                                                                    <div>

                                                                        <h6 className="mb-0 fw-semibold text-primary">
                                                                            New Assignment
                                                                        </h6>

                                                                        <small className="text-muted">
                                                                            Select new bus and stop
                                                                        </small>

                                                                    </div>

                                                                </div>

                                                            </div>

                                                            <div className="card-body">

                                                                {/* New Bus */}

                                                                <div className="mb-3">

                                                                    <label className="form-label fw-semibold">
                                                                        New Bus
                                                                        <span className="text-danger ms-1">
                                                                            *
                                                                        </span>
                                                                    </label>

                                                                    <select
                                                                        className="form-select"
                                                                        disabled={transferBlocked}
                                                                        value={
                                                                            form?.new_bus_id ||
                                                                            ""
                                                                        }
                                                                        onChange={(e) =>
                                                                            handleNewBusChange(
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        required
                                                                    >

                                                                        <option value="">
                                                                            Select New Bus
                                                                        </option>

                                                                        {buses.map(
                                                                            (bus) => (
                                                                                <option
                                                                                    key={bus?.id}
                                                                                    value={bus?.id}
                                                                                    disabled={
                                                                                        Number(
                                                                                            bus?.id
                                                                                        ) ===
                                                                                        Number(
                                                                                            form?.current_bus_id
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {bus?.bus_number ??
                                                                                        bus?.title ??
                                                                                        `Bus #${bus?.id}`}
                                                                                    {bus?.driver_name
                                                                                        ? ` — ${bus.driver_name}`
                                                                                        : ""}
                                                                                </option>
                                                                            )
                                                                        )}

                                                                    </select>

                                                                </div>

                                                                {/* New Stop */}

                                                                <div className="mb-3">

                                                                    <label className="form-label fw-semibold">
                                                                        New Stop
                                                                        <span className="text-danger ms-1">
                                                                            *
                                                                        </span>
                                                                    </label>

                                                                    <select
                                                                        className="form-select"
                                                                        value={
                                                                            form?.new_stop_id ||
                                                                            ""
                                                                        }
                                                                        onChange={(e) =>
                                                                            handleNewStopChange(
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            transferBlocked || !form?.new_bus_id
                                                                        }
                                                                        required
                                                                    >

                                                                        <option value="">
                                                                            {form?.new_bus_id
                                                                                ? "Select New Stop"
                                                                                : "First Select New Bus"}
                                                                        </option>

                                                                        {newBusStops.map(
                                                                            (stop) => (
                                                                                <option
                                                                                    key={
                                                                                        stop?.id
                                                                                    }
                                                                                    value={
                                                                                        stop?.id
                                                                                    }
                                                                                >
                                                                                    {stop?.stop_name ??
                                                                                        stop?.title ??
                                                                                        `Stop #${stop?.id}`}
                                                                                </option>
                                                                            )
                                                                        )}

                                                                    </select>

                                                                    {form?.new_bus_id &&
                                                                        newBusStops.length ===
                                                                        0 && (
                                                                            <small className="text-danger mt-1 d-block">
                                                                                No stops found for
                                                                                this bus.
                                                                            </small>
                                                                        )}

                                                                </div>

                                                                {/* New Bus Info */}

                                                                {newBus && (

                                                                    <div className="rounded-3 bg-primary-subtle p-3">

                                                                        <div className="row g-3">

                                                                            <div className="col-6">

                                                                                <small className="text-muted d-block">
                                                                                    Driver
                                                                                </small>

                                                                                <strong>
                                                                                    {newBus
                                                                                        ?.driver_name ??
                                                                                        "-"}
                                                                                </strong>

                                                                            </div>

                                                                            <div className="col-6">

                                                                                <small className="text-muted d-block">
                                                                                    Mobile
                                                                                </small>

                                                                                <strong>
                                                                                    {newBus
                                                                                        ?.driver_number ??
                                                                                        "-"}
                                                                                </strong>

                                                                            </div>

                                                                            <div className="col-12">

                                                                                <small className="text-muted d-block">
                                                                                    Selected Stop
                                                                                </small>

                                                                                <strong>
                                                                                    {newStop
                                                                                        ?.stop_name ??
                                                                                        "Not selected"}
                                                                                </strong>

                                                                            </div>

                                                                        </div>

                                                                    </div>

                                                                )}

                                                                {/* New Fee */}

                                                                <div className="row g-3 align-items-end">
                                                                    <div className="col-12 col-md-3">
                                                                        <small className="text-muted d-block">
                                                                            Stop Fee
                                                                        </small>
                                                                        <strong className="text-primary fs-5">
                                                                            {money(newStopFee)}
                                                                        </strong>
                                                                    </div>

                                                                    <div className="col-12 col-md-3">
                                                                        <small className="text-muted d-block">
                                                                            Final Fee
                                                                        </small>
                                                                        <strong className="text-success fs-5">
                                                                            {money(newFinalFee)}
                                                                        </strong>
                                                                    </div>

                                                                    <div className="col-12 col-md-3">
                                                                        <label className="form-label small text-muted mb-1">
                                                                            Discount
                                                                        </label>

                                                                        <div className="input-group">
                                                                            <span className="input-group-text">₹</span>
                                                                            <input
                                                                                type="number"
                                                                                className="form-control"
                                                                                min="0"
                                                                                max={newStopFee}
                                                                                step="1"
                                                                                value={newDiscount}
                                                                                disabled={transferBlocked || !form?.new_stop_id}
                                                                                onChange={(e) =>
                                                                                    handleNewDiscountChange(
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                                placeholder="0"
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-12 col-md-3">
                                                                        <label className="form-label small text-muted mb-1">
                                                                            Discount Remark
                                                                            {Number(newDiscount || 0) > 0 && (
                                                                                <span className="text-danger ms-1">
                                                                                    *
                                                                                </span>
                                                                            )}
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            value={form?.discount_remark || ""}
                                                                            disabled={transferBlocked || !form?.new_stop_id}
                                                                            onChange={(e) =>
                                                                                handleChange(
                                                                                    "discount_remark",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            required={Number(newDiscount || 0) > 0}
                                                                            placeholder={
                                                                                Number(newDiscount || 0) > 0
                                                                                    ? "Please enter discount reason..."
                                                                                    : "Enter discount remark (optional)"
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="border-top mt-3 pt-3">
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <small className="text-muted">
                                                                            {money(newStopFee)} − {money(newDiscount)}
                                                                        </small>
                                                                        <strong className="text-success">
                                                                            {money(newFinalFee)}
                                                                        </strong>
                                                                    </div>
                                                                </div>

                                                            </div>

                                                        </div>

                                                    </div>



                                                </div>




                                                <div className="card border-0 shadow-sm mb-4">

                                                    <div className="card-header py-3 border-bottom">

                                                        <div className="d-flex align-items-center gap-2">

                                                            <div className="text-primary">
                                                                <i className="ri-file-edit-line fs-5"></i>
                                                            </div>

                                                            <div>

                                                                <h6 className="mb-0 fw-semibold">
                                                                    Transfer Details
                                                                </h6>

                                                                <small className="text-muted">
                                                                    Set date, payment type and reason
                                                                </small>

                                                            </div>

                                                        </div>

                                                    </div>

                                                    <div className="card-body">

                                                        <div className="row g-3">

                                                            {/* Date */}

                                                            <div className="col-12 col-md-6">

                                                                <label className="form-label fw-semibold">
                                                                    Joining Date
                                                                    <span className="text-danger ms-1">
                                                                        *
                                                                    </span>
                                                                </label>

                                                                <input
                                                                    type="date"
                                                                    className="form-control"
                                                                    value={
                                                                        form?.joining_date ||
                                                                        ""
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleChange(
                                                                            "joining_date",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    required
                                                                />

                                                            </div>


                                                            {/* Payment */}

                                                            <div className="col-12 col-md-6">

                                                                <label className="form-label fw-semibold">
                                                                    Payment Type
                                                                    <span className="text-danger ms-1">
                                                                        *
                                                                    </span>
                                                                </label>

                                                                <select
                                                                    className="form-select"
                                                                    disabled={transferBlocked}
                                                                    value={
                                                                        form?.payment_type ||
                                                                        ""
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleChange(
                                                                            "payment_type",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    required
                                                                >

                                                                    <option value="">
                                                                        Select Payment Type
                                                                    </option>

                                                                    <option value="Monthly">
                                                                        Monthly
                                                                    </option>

                                                                    <option value="Quarterly">
                                                                        Quarterly
                                                                    </option>

                                                                    <option value="Half Year">
                                                                        Half Year
                                                                    </option>

                                                                    <option value="Full Year">
                                                                        Full Year
                                                                    </option>

                                                                </select>

                                                            </div>


                                                            {/* Reason */}

                                                            <div className="col-12">

                                                                <label className="form-label fw-semibold">
                                                                    Transfer Reason
                                                                    <span className="text-danger ms-1">
                                                                        *
                                                                    </span>
                                                                </label>

                                                                <textarea
                                                                    className="form-control"
                                                                    rows="3"
                                                                    disabled={transferBlocked}
                                                                    placeholder="Enter transfer reason / remark..."
                                                                    value={
                                                                        form?.reason ||
                                                                        ""
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleChange(
                                                                            "reason",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    required
                                                                />

                                                            </div>

                                                        </div>

                                                    </div>

                                                </div>


                                                <div className="card border-0 shadow-sm mb-4">

                                                    <div className="card-body">

                                                        <div className="text-center mb-3">

                                                            <small className="text-muted fw-semibold">
                                                                TRANSFER PREVIEW
                                                            </small>

                                                        </div>

                                                        <div className="row align-items-center g-3">

                                                            <div className="col-12 col-md-5">

                                                                <div className="border rounded-3 p-3">

                                                                    <div className="d-flex align-items-center gap-3">

                                                                        <div
                                                                            className="bg-warning-subtle text-warning rounded-3 d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                width: "44px",
                                                                                height: "44px",
                                                                            }}
                                                                        >
                                                                            <i className="ri-bus-line fs-5"></i>
                                                                        </div>

                                                                        <div>

                                                                            <small className="text-muted d-block">
                                                                                Current
                                                                            </small>

                                                                            <strong>
                                                                                {currentBus
                                                                                    ?.bus_number ??
                                                                                    "Current Bus"}
                                                                            </strong>

                                                                            <small className="text-muted d-block">
                                                                                {currentStop
                                                                                    ?.stop_name ??
                                                                                    "Current Stop"}
                                                                            </small>

                                                                        </div>

                                                                    </div>

                                                                </div>

                                                            </div>


                                                            <div className="col-12 col-md-2 text-center">

                                                                <div
                                                                    className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: "44px",
                                                                        height: "44px",
                                                                    }}
                                                                >
                                                                    <i className="ri-arrow-right-line fs-5"></i>
                                                                </div>

                                                            </div>


                                                            <div className="col-12 col-md-5">

                                                                <div className="border border-primary rounded-3 p-3 bg-primary-subtle">

                                                                    <div className="d-flex align-items-center gap-3">

                                                                        <div
                                                                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center"
                                                                            style={{
                                                                                width: "44px",
                                                                                height: "44px",
                                                                            }}
                                                                        >
                                                                            <i className="ri-bus-line fs-5"></i>
                                                                        </div>

                                                                        <div>

                                                                            <small className="text-muted d-block">
                                                                                New
                                                                            </small>

                                                                            <strong>
                                                                                {newBus
                                                                                    ?.bus_number ??
                                                                                    "New Bus"}
                                                                            </strong>

                                                                            <small className="text-muted d-block">
                                                                                {newStop
                                                                                    ?.stop_name ??
                                                                                    "New Stop"}
                                                                            </small>

                                                                        </div>

                                                                    </div>

                                                                </div>

                                                            </div>

                                                        </div>

                                                        {/* New Fee Creation Preview */}
                                                        {feePreview.length > 0 && (
                                                            <div className="mt-4 border rounded-3 overflow-hidden">
                                                                <div className="p-3 bg-light border-bottom">
                                                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                                                        <div>
                                                                            <strong>New Bus Fee Details Preview</strong>
                                                                            <div className="small text-muted">
                                                                                Old fee details will be removed and new records will be created for 12 months from the joining month according to the selected payment type.
                                                                            </div>
                                                                        </div>
                                                                        <span className="badge bg-primary">
                                                                            {form?.payment_type}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="table-responsive">
                                                                    <table className="table table-sm align-middle mb-0">
                                                                        <thead className="table-light">
                                                                            <tr>
                                                                                <th>#</th>
                                                                                <th>Month</th>
                                                                                <th>Year</th>
                                                                                <th>Type</th>
                                                                                <th>Actual Amount</th>
                                                                                <th>Discount</th>
                                                                                <th>Final Amount</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {feePreview.map((item, index) => (
                                                                                <tr key={`${item?.year}-${item?.month}-${index}`}>
                                                                                    <td>{index + 1}</td>
                                                                                    <td>
                                                                                        {String(item?.month || "").padStart(2, "0")}-{item?.year || ""}
                                                                                    </td>
                                                                                    <td>{item?.year}</td>
                                                                                    <td>{item?.type}</td>
                                                                                    <td>{money(item?.actual)}</td>
                                                                                    <td>{money(item?.discount)}</td>
                                                                                    <td className="text-success fw-semibold">
                                                                                        {money(item?.final)}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>

                                                                <div className="p-3 border-top bg-light">
                                                                    <div className="row g-3">
                                                                        <div className="col-12 col-md-4">
                                                                            <small className="text-muted d-block">
                                                                                Total Actual
                                                                            </small>
                                                                            <strong>
                                                                                {money(
                                                                                    feePreview.reduce(
                                                                                        (sum, item) =>
                                                                                            sum + Number(item?.actual || 0),
                                                                                        0
                                                                                    )
                                                                                )}
                                                                            </strong>
                                                                        </div>

                                                                        <div className="col-12 col-md-4">
                                                                            <small className="text-muted d-block">
                                                                                Total Discount
                                                                            </small>
                                                                            <strong className="text-warning">
                                                                                {money(
                                                                                    feePreview.reduce(
                                                                                        (sum, item) =>
                                                                                            sum + Number(item?.discount || 0),
                                                                                        0
                                                                                    )
                                                                                )}
                                                                            </strong>
                                                                        </div>

                                                                        <div className="col-12 col-md-4">
                                                                            <small className="text-muted d-block">
                                                                                Total Final
                                                                            </small>
                                                                            <strong className="text-success fs-5">
                                                                                {money(
                                                                                    feePreview.reduce(
                                                                                        (sum, item) =>
                                                                                            sum + Number(item?.final || 0),
                                                                                        0
                                                                                    )
                                                                                )}
                                                                            </strong>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>

                                                {transferBlocked ? (

                                                    <div className="alert alert-danger border-0 shadow-sm d-flex align-items-center gap-2 mb-4">

                                                        <i className="ri-close-circle-line fs-5"></i>

                                                        <div>

                                                            <strong>
                                                                Transfer Not Allowed
                                                            </strong>

                                                            <div className="small">
                                                                Paid transport amount is {money(currentPaidFee)}.
                                                                Student cannot be transferred because paid amount is greater than ₹0.
                                                            </div>

                                                        </div>

                                                    </div>

                                                ) : (

                                                    <div className="alert alert-success border-0 shadow-sm d-flex align-items-center gap-2 mb-4">

                                                        <i className="ri-checkbox-circle-line fs-5"></i>

                                                        <div>

                                                            <strong>
                                                                Transfer Allowed
                                                            </strong>

                                                            <div className="small">
                                                                Paid transport amount is ₹0, so transfer is allowed.
                                                            </div>

                                                        </div>

                                                    </div>

                                                )}



                                                <div className="card border-0 shadow-sm">

                                                    <div className="card-body">

                                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

                                                            <div className="text-muted small">

                                                                <i className="ri-information-line me-1"></i>

                                                                Check bus, stop and transfer details
                                                                before saving.

                                                            </div>

                                                            <div className="d-flex gap-2">

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary"
                                                                    onClick={
                                                                        handleReset
                                                                    }
                                                                >
                                                                    <i className="ri-refresh-line me-1"></i>
                                                                    Reset
                                                                </button>

                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary px-4"
                                                                    disabled={
                                                                        loading ||
                                                                        transferBlocked ||
                                                                        !selectedStudentId ||
                                                                        !currentEnrollment?.id ||
                                                                        !form?.current_bus_id ||
                                                                        !form?.current_stop_id ||
                                                                        !form?.new_bus_id ||
                                                                        !form?.new_stop_id ||
                                                                        !form?.joining_date ||
                                                                        !form?.payment_type ||
                                                                        !form?.reason?.trim()
                                                                    }
                                                                >
                                                                    <i className="ri-shuffle-line me-1"></i>
                                                                    Transfer Student
                                                                </button>

                                                            </div>

                                                        </div>

                                                    </div>

                                                </div>

                                            </form>

                                        )}

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>
            </div >
        </>
    );
}